import { useState } from 'react';
import { CheckCircle2, AlertTriangle, RefreshCw, Database, Server } from 'lucide-react';

import { probeDocSyncReplicas, type DocSyncReplicaStatus } from '../../services/api/documents';
import { Button, Card } from '../../components/ui';

const PROBE_SAMPLE_COUNT = 8;

const formatTimestamp = (iso: string | null | undefined): string => {
  if (!iso) return '–';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '–';
    return new Intl.DateTimeFormat('sv-SE', {
      dateStyle: 'short',
      timeStyle: 'medium',
    }).format(d);
  } catch {
    return '–';
  }
};

const truncateVersion = (version: string | null | undefined): string => {
  if (!version) return '–';
  if (version.length <= 24) return version;
  return `${version.slice(0, 11)}…${version.slice(-8)}`;
};

export const DocSyncStatusPanel = () => {
  const [replicas, setReplicas] = useState<DocSyncReplicaStatus[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastProbedAt, setLastProbedAt] = useState<string | null>(null);

  const handleProbe = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await probeDocSyncReplicas(PROBE_SAMPLE_COUNT);
      setReplicas(result);
      setLastProbedAt(new Date().toISOString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Okänt fel vid hälsoprobning');
    } finally {
      setLoading(false);
    }
  };

  const canonicalVersion = replicas?.find((r) => r.supabaseVersion)?.supabaseVersion ?? null;
  const canonicalUpdatedAt = replicas?.find((r) => r.supabaseUpdatedAt)?.supabaseUpdatedAt ?? null;
  const canonicalTrigger =
    replicas?.find((r) => r.supabaseTriggeredBy)?.supabaseTriggeredBy ?? null;

  const outOfSyncReplicas = replicas?.filter((r) => !r.inSync) ?? [];
  const allInSync = replicas !== null && replicas.length > 0 && outOfSyncReplicas.length === 0;

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Server className="h-5 w-5 text-brand-blue" />
            Doc-Sync Health
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            Verifiera att alla backend-replikor har de senaste synkade dokumenten i minnet. Pingar
            API:et {PROBE_SAMPLE_COUNT} gånger för att fånga olika replikor via load balancer.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={handleProbe}
          disabled={loading}
        >
          {loading ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Pingar…
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Verifiera health
            </>
          )}
        </Button>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 mb-4">
          {error}
        </div>
      ) : null}

      {replicas === null && !loading ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500 text-center">
          Tryck <strong>Verifiera health</strong> för att kolla nuvarande synk-status över alla
          replikor.
        </div>
      ) : null}

      {replicas !== null ? (
        <div className="space-y-4">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
              <Database className="h-4 w-4" />
              Sanning i Supabase
            </div>
            <div className="font-mono text-sm text-slate-900 break-all">
              {canonicalVersion ?? '(saknas — Supabase ej tillgänglig)'}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Senast bumpad: {formatTimestamp(canonicalUpdatedAt)}
              {canonicalTrigger ? ` · trigger: ${canonicalTrigger}` : ''}
            </div>
          </div>

          {allInSync ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-emerald-900">
                  Alla {replicas.length} replikor är i sync ✓
                </div>
                <div className="text-xs text-emerald-700 mt-0.5">
                  Senast verifierat: {formatTimestamp(lastProbedAt)}
                </div>
              </div>
            </div>
          ) : outOfSyncReplicas.length > 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-amber-900">
                  {outOfSyncReplicas.length} av {replicas.length} replikor släpar efter
                </div>
                <div className="text-xs text-amber-700 mt-0.5">
                  Watcher-pollen kör var 30:e sekund — vänta in nästa intervall, eller tryck
                  Verifiera igen.
                </div>
              </div>
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left">Replica</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Last seen version</th>
                  <th className="px-3 py-2 text-left">Loaded at</th>
                  <th className="px-3 py-2 text-right">Docs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {replicas.map((r) => (
                  <tr key={r.replicaId}>
                    <td className="px-3 py-2 font-mono text-xs text-slate-700">
                      {r.replicaId.slice(0, 8)}…
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        module {r.moduleInstanceId}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {r.inSync ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          In sync
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-semibold">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          Stale
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-700">
                      {truncateVersion(r.lastSeenVersion)}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {formatTimestamp(r.loadedAt)}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-slate-600">
                      {r.docCount.toLocaleString('sv-SE')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-[11px] text-slate-400">
            Pingade {PROBE_SAMPLE_COUNT} ggr · hittade {replicas.length} unika replikor. Render's
            load balancer kan vara sticky inom korta fönster så fler replikor kan finnas än vad
            probningen sett.
          </p>
        </div>
      ) : null}
    </Card>
  );
};
